<template>
  <div v-show="props.totalCount > 0" class="personal-area-table-pagination">
    <button
      class="personal-area-table-pagination__previous"
      :disabled="currentPage === 1"
      @click="goToPage(currentPage - 1)"
    >
      <PaginationLeftIcon />
    </button>

    <button
      v-for="page in visiblePages"
      :key="pageKey(page)"
      class="personal-area-table-pagination__page"
      :class="{ active: page === currentPage }"
      :disabled="page === '...'"
      @click="onClick(page)"
    >
      {{ page }}
    </button>

    <button
      class="personal-area-table-pagination__next"
      :disabled="currentPage === totalPages"
      @click="goToPage(currentPage + 1)"
    >
      <PaginationRightIcon />
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import PaginationLeftIcon from '@/js/components/icons/PaginationLeftIcon.vue';
import PaginationRightIcon from '@/js/components/icons/PaginationRightIcon.vue';

type PageItem = number | '...';

const props = defineProps<{
  modelValue: number;
  totalCount: number;
  pageSize: number;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void;
  (e: 'change', value: number): void;
}>();

const currentPage = computed(() => props.modelValue);

const totalPages = computed(() => Math.ceil(props.totalCount / props.pageSize));

const visiblePages = computed<PageItem[]>(() => {
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 6) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: PageItem[] = [1];

  if (current > 4) pages.push('...');

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (current < total - 3) pages.push('...');

  pages.push(total);

  return pages;
});

function goToPage(page: number) {
  const safePage = Math.max(1, Math.min(page, totalPages.value));
  emit('update:modelValue', safePage);
  emit('change', safePage);
}

function onClick(page: PageItem) {
  if (page === '...') return;
  goToPage(page);
}

function pageKey(page: PageItem) {
  return typeof page === 'number' ? page : `dots-${Math.random()}`;
}
</script>

<style scoped lang="scss">
.personal-area-table-pagination {
  border: none;
  display: flex;
  gap: 8px;

  &__previous,
  &__next,
  &__page {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    display: flex;
    padding-top: 10px;
    padding-bottom: 11px;
    align-items: center;
    justify-content: center;
    border: none;
    background: #fff;
    color: var(--stc-black);
    font-family: var(--stc-font-semibold);
    font-weight: 500;
    font-size: 14px;
  }

  &__previous,
  &__next {
    width: 32px;
    height: 32px;
    padding: 4px;
    padding-top: 4px;
    padding-bottom: 4px;
    border-radius: 8px;
    &:disabled {
      color: var(--stc-gray-300);
    }
  }

  &__previous:not(:disabled):hover,
  &__next:not(:disabled):hover,
  &__page:not(:disabled):hover {
    border: 1px solid var(--stc-brand-600) !important;
  }

  &__page {
    border-radius: 8px;

    &.active {
      border: 1px solid var(--stc-brand-600);
      color: var(--stc-brand-600);
    }

    &.active:hover {
      border: 1px solid var(--stc-brand-600) !important;
    }
  }
}
</style>
